import { LoginUserResponse } from '@api-client/api'
import { LocalStorageCache } from '@auth0/auth0-react'
import { jwtDecode, JwtPayload } from 'jwt-decode'

const readToken = (token: string): JwtPayload | undefined => {
	if (!token) {
		return
	}
	return jwtDecode<JwtPayload>(token)
}

interface Auth0Tokens {
	access_token: string
	refresh_token: string
	expires_in: number
	token_type: string
	oauthTokenScope: string
}
interface Auth0IdTokenLocalStorage {
	id_token: string
}
interface Auth0TokensLocalStorage {
	body: Auth0Tokens
}
type TokenClaims = {
	family_name?: string
	given_name?: string
	email?: string
} & JwtPayload
// this constant hold a reference in 05-refresh-token-on-navigation.cy.js. Please update the value if any change.
const ACCESS_TOKEN = 'access_token'
const REFRESH_TOKEN = 'refresh_token'
const ID_TOKEN = 'id_token'

// offline_access is required to get a refresh token and silently refresh user session
export const LOGIN_SCOPE = 'openid profile email offline_access'
export const CO2AI_CONNECTION_NAME = 'pe-okta'
export const CDP_CONNECTION_NAME = 'pe-cdp'

const logout = (): void => {
	localStorage.removeItem(ACCESS_TOKEN)
	localStorage.removeItem(REFRESH_TOKEN)
	localStorage.removeItem(ID_TOKEN)
}

const isAuthenticated = (): boolean => {
	const token = localStorage.getItem(ACCESS_TOKEN)
	if (token) {
		const accessToken = readToken(token)
		if (accessToken) {
			const exp_date = accessToken.exp
			if (exp_date) {
				const now = Date.now() / 1000
				if (accessToken && now < exp_date) {
					return true
				}
				return false
			}
		}
	}
	return false
}

const getRefreshToken = (): string | undefined =>
	localStorage.getItem(REFRESH_TOKEN) || undefined

const getAccessToken = (): string | undefined =>
	localStorage.getItem(ACCESS_TOKEN) || undefined

const clearAccessToken = () => {
	localStorage.removeItem(ACCESS_TOKEN)
}

const storeToken = (loginResponse: LoginUserResponse) => {
	localStorage.setItem(ACCESS_TOKEN, loginResponse.access_token)
	localStorage.setItem(ID_TOKEN, loginResponse.id_token)
	localStorage.setItem(REFRESH_TOKEN, loginResponse.refresh_token)
}

const getIdTokenClaims = (): TokenClaims => {
	const token = localStorage.getItem(ID_TOKEN)
	if (!token) {
		return {}
	}
	const idToken = readToken(token)
	if (!idToken) {
		return {}
	}
	return idToken
}

const storeAuth0TokensInLocalStorage = () => {
	// Retrieve all keys stored by auth0-react in local storage
	// Auth0-react stores two keys in local storage: auth0spa (idToken) and auth0spa@accessToken (accessToken + refreshToken)
	// We parse them below and store them in our standardized object LoginResponse
	const loginResponse: LoginUserResponse = {
		access_token: '',
		refresh_token: '',
		id_token: '',
		expires_in: 0,
		token_type: '',
		scope: '',
	}
	const localStorageCache = new LocalStorageCache()
	const auth0StorageKeys = localStorageCache
		.allKeys()
		.filter(key => key.includes('auth0spa'))
	for (const key of auth0StorageKeys) {
		const currentStorage = localStorageCache.get(key as string)
		if (currentStorage?.hasOwnProperty('body')) {
			const tokens = currentStorage as unknown as Auth0TokensLocalStorage
			loginResponse.access_token = tokens?.body?.access_token
			loginResponse.refresh_token = tokens?.body?.refresh_token
			loginResponse.expires_in = tokens?.body?.expires_in
			loginResponse.token_type = tokens?.body?.token_type
			loginResponse.scope = tokens?.body?.oauthTokenScope
		}
		if (currentStorage?.hasOwnProperty('id_token')) {
			loginResponse.id_token = (
				currentStorage as unknown as Auth0IdTokenLocalStorage
			).id_token
		}
	}
	storeToken(loginResponse)
}
export default {
	getAccessToken,
	getIdTokenClaims,
	getRefreshToken,
	isAuthenticated,
	logout,
	storeToken,
	storeAuth0TokensInLocalStorage,
	clearAccessToken,
}
